import React, { useRef, useState } from 'react'
import { useParams } from 'react-router'
import { trans } from 'sharedKernel/i18n'
import { Loading } from 'components/loading'
import { showToast } from 'components/toast'
import { PageTitle } from 'domain/feature/hub/pageTitle/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  buildRegisterInterestRequest,
  useGetStartupOpportunityById,
  useGetStartupOpportunityFiles,
  useRegisterInterest,
} from '../api'
import { normalizeExternalUrl } from 'domain/feature/hub/company/scouting/scoutingOpportunityDisplay'
import { formatDateForDisplay } from 'sharedKernel/handlers'
import { displayOrDash } from '../behavior'
import { OpportunityDetailAbout } from './components/OpportunityDetailAbout'
import { OpportunityDetailCover } from './components/OpportunityDetailCover'
import { OpportunityDetailMeta } from './components/OpportunityDetailMeta'
import { MobileContainerPadding } from '../style'
import {
  DetailBodyGrid,
  DetailDescriptionColumn,
  DetailFullPageRoot,
  DetailHeader,
  DetailPageWrapper,
  InterestButton,
  InterestCardColumn,
  InterestCardDeadline,
  InterestCardTitle,
  InterestSidebarCard,
  MetaBarColumn,
  OpportunityDetailGlobalStyle,
} from './style'

const SUPPORT_LINK_OPEN_DELAY_MS = 1500

export const HubStartupOpportunityDetailFeature = (): JSX.Element => {
  const { opportunityId = '' } = useParams()
  const [interestJustRegistered, setInterestJustRegistered] = useState(false)
  const pendingSupportLinkRef = useRef<string | undefined>(undefined)

  const {
    data: opportunity,
    isFetching: isOpportunityFetching,
  } = useGetStartupOpportunityById(opportunityId)

  const {
    data: fileRows,
    isFetching: isFilesFetching,
  } = useGetStartupOpportunityFiles(opportunityId)

  const { mutate: registerInterest, isLoading: isRegisteringInterest } = useRegisterInterest(
    async () => {
      setInterestJustRegistered(true)
      showToast('success', trans('scouting.opportunity.startup.toast.interestSuccess'))
      setTimeout(() => {
        const link = pendingSupportLinkRef.current
        if (link) {
          window.open(normalizeExternalUrl(link), '_blank', 'noopener,noreferrer')
        }
      }, SUPPORT_LINK_OPEN_DELAY_MS)
    },
    async () => {
      pendingSupportLinkRef.current = undefined
      showToast('error', trans('scouting.opportunity.startup.toast.interestError'))
    }
  )

  if (isOpportunityFetching || isFilesFetching) {
    return <Loading />
  }

  if (!opportunity) {
    return (
      <DetailFullPageRoot className="hub-startup-opportunity-detail-root">
        <MobileContainerPadding />
        <OpportunityDetailGlobalStyle />
        <div className="alert alert-warning">
          {trans('scouting.opportunity.startup.detailNotFound')}
        </div>
      </DetailFullPageRoot>
    )
  }

  const isInterestRegistered = (opportunity.hasInterest ?? false) || interestJustRegistered
  const deadlineDate = displayOrDash(
    opportunity.interestDeadline ? formatDateForDisplay(opportunity.interestDeadline) || null : null
  )

  const handleRegisterInterest = (): void => {
    pendingSupportLinkRef.current = opportunity.supportLink?.trim() || undefined
    registerInterest(buildRegisterInterestRequest(opportunity.id))
  }

  return (
    <DetailFullPageRoot className="hub-startup-opportunity-detail-root">
      <MobileContainerPadding />
      <OpportunityDetailGlobalStyle />
      <DetailPageWrapper>
        <DetailHeader>
          <PageTitle>{opportunity.title}</PageTitle>
        </DetailHeader>

        <OpportunityDetailCover opportunity={opportunity} fileRows={fileRows} />

        <DetailBodyGrid>
          <MetaBarColumn>
            <OpportunityDetailMeta opportunity={opportunity} />
            <DetailDescriptionColumn>
              <OpportunityDetailAbout
                opportunity={opportunity}
                fileRows={fileRows}
                showCover={false}
                showMeta={false}
                compactBelowMeta
              />
            </DetailDescriptionColumn>
          </MetaBarColumn>

          <InterestCardColumn>
            <InterestSidebarCard>
              <InterestCardTitle>
                {trans('scouting.opportunity.startup.interestCard.title')}
              </InterestCardTitle>
              <InterestCardDeadline>
                {trans('scouting.opportunity.startup.interestCard.deadline')} {deadlineDate}
              </InterestCardDeadline>
              <InterestButton
                type="button"
                $registered={isInterestRegistered}
                disabled={isInterestRegistered || isRegisteringInterest}
                onClick={handleRegisterInterest}
              >
                {isInterestRegistered && <FontAwesomeIcon icon={['fas', 'circle-check']} />}
                {isInterestRegistered
                  ? trans('scouting.opportunity.startup.interestCard.registered')
                  : trans('scouting.opportunity.modal.registerInterest')}
              </InterestButton>
            </InterestSidebarCard>
          </InterestCardColumn>
        </DetailBodyGrid>
      </DetailPageWrapper>
    </DetailFullPageRoot>
  )
}
